import { useParams } from "react-router-dom";
import {
    FormControl,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    SelectChangeEvent
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import "../../../assets/styles/registrationRequest/registrationRequest.scss";
import { useLeadReceiptsInfo } from "../service/LeadReceiptService";
import { convertLeadReceiptToFormData } from "../utils/mapper";
import { LeadType } from "types/leadType";
import { getProductIdFromProductName } from "types/product";
import { ILeadReceipt } from "../service/dtos";
import { useSupplierInfo } from "layout/context/SupplierInfoContext";
import {
    RegistrationRequest,
    useSendSendRegistrationRequestToHunterPullLogic
} from "../service/RegistrationRequestService";
import RegistrationRequestForm, {
    IRegistrationRequestFormData
} from "./RegistrationRequestForm";
import RegistrationRequestConfirmationModal from "./RegistrationRequestConfirmationModal";

interface IRegistrationRequestFormModalProps {
    popupOpen: boolean;
    leadType: LeadType;
    secureId: string;
    requestNumber: string;
    productName: string;
    setPopupOpen: (open: boolean) => void;
    onEmailSend: () => void;
    onEmailError: () => void;
}

function RegistrationRequestModal({
    popupOpen,
    leadType,
    setPopupOpen,
    onEmailSend,
    onEmailError,
    secureId,
    requestNumber,
    productName
}: IRegistrationRequestFormModalProps) {
    const { supplierId: supplierIdFromParams } = useParams<{
        supplierId?: string;
    }>();
    const { supplierId: supplierIdFromContext, supplierName } =
        useSupplierInfo();
    const effectiveSupplierId: string =
        supplierIdFromParams || supplierIdFromContext || "";
    const { data: leadReceiptsInfo, isSuccess } = useLeadReceiptsInfo(
        effectiveSupplierId.toString()
    );
    const leadReceipts = leadReceiptsInfo?.waitingListBookingLeadReceipts ?? [];
    const [selectedContactId, setSelectedContactId] = useState<string>("");
    const [confirmationModel, setConfirmationModel] = useState<boolean>(false);
    const [selectedContact, setSelectedContact] = useState<
        ILeadReceipt | undefined
    >(undefined);
    const [hasMultipleContacts, setHasMultipleContacts] = useState(false);

    useEffect(() => {
        if (isSuccess && leadReceipts.length > 0) {
            // Set the default selected contact id
            setSelectedContactId(leadReceipts[0].id);
        }

        // Once the selectedContactId is updated, compute the selected contact
        if (selectedContactId) {
            const contact = leadReceipts.find(
                (x) => x.id === selectedContactId
            );
            setSelectedContact(contact);
        }

        // Determine if there are multiple contacts
        setHasMultipleContacts(leadReceipts.length > 1);
    }, [isSuccess, leadReceipts, selectedContactId]);

    const handleSuccess = () => {
        setConfirmationModel(true);
        setPopupOpen(false);
        onEmailSend();
    };

    const handleError = () => {
        setPopupOpen(false);
        onEmailError();
    };

    const { mutate: sendRegistrationRequest } =
        useSendSendRegistrationRequestToHunterPullLogic({
            onSuccess: () => {
                handleSuccess();
            },
            onError: () => {
                handleError();
            }
        });

    const handleSubmit = (formData: IRegistrationRequestFormData) => {
        const produdctId = getProductIdFromProductName(productName);
        if (produdctId != undefined) {
            const registrationRequest: RegistrationRequest = {
                productId: produdctId,
                requestNumber: requestNumber,
                supplierId: effectiveSupplierId.toString(),
                contactName: formData.contactName,
                supplierPhoneNumber: formData.mobileNumber,
                supplierEmailAddress: formData.emailAddress,
                companyName: formData.companyName,
                leadType: leadType
            };
            sendRegistrationRequest(registrationRequest);
        } else {
            console.error("productId is undefiend!");
        }
    };

    const handleContactChange = (event: SelectChangeEvent<string>) => {
        setSelectedContactId(event.target.value);
    };

    return (
        <div>
            <Modal
                className="modal-container"
                open={popupOpen}
                aria-labelledby="pre-lunch-registration-popup"
                aria-describedby="pre-lunch-registration-popup-information"
            >
                <div className="modal-body">
                    <div
                        className="icon icon-close"
                        onClick={() => setPopupOpen(false)}
                    ></div>
                    <h2 id="pre-lunch-registration-title">
                        Erhalten Sie alle Informationen für Ihren
                        Anfragen-Manager
                    </h2>
                    {hasMultipleContacts && (
                        <div className="mb-20">
                            <div className="col-7 label-in-row-desktop contact-person-dropdown-form">
                                <label className="bolder">
                                    Ansprechpartner:
                                </label>
                                <FormControl
                                    fullWidth
                                    margin="normal"
                                    className="mtb-15"
                                >
                                    <InputLabel id="contact-dropdown-label">
                                        Ansprechpartner
                                    </InputLabel>
                                    <Select
                                        labelId="contact-dropdown-label"
                                        className="contact-person-dropdown"
                                        value={selectedContactId}
                                        onChange={handleContactChange}
                                    >
                                        {leadReceipts.map((receipt) => (
                                            <MenuItem
                                                key={receipt.id}
                                                value={receipt.id}
                                            >
                                                {receipt.email}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                            <hr />
                        </div>
                    )}
                    <RegistrationRequestForm
                        key={selectedContactId}
                        RegistrationRequestFormData={convertLeadReceiptToFormData(
                            selectedContact,
                            effectiveSupplierId.toString(),
                            secureId,
                            supplierName,
                            requestNumber
                        )}
                        onSubmit={handleSubmit}
                    />
                </div>
            </Modal>
            <RegistrationRequestConfirmationModal
                popUpOpen={confirmationModel}
                setPopUpOpen={setConfirmationModel}
            />
        </div>
    );
}

export default RegistrationRequestModal;
